import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, Zap, Sparkles, RotateCcw } from "lucide-react";

const formatCHF = (n) =>
  new Intl.NumberFormat("fr-CH", { style: "currency", currency: "CHF", maximumFractionDigits: 0 })
    .format(Math.round(n || 0));

const situations = [
  { v: "celibataire", t: "Célibataire", adultes: 1 },
  { v: "couple", t: "Couple", adultes: 2 },
  { v: "famille", t: "Famille", adultes: 2 },
];

const revenuRanges = [
  { v: "low", t: "< 4'000", code: 3500 },
  { v: "mid", t: "4'000 – 7'000", code: 5500 },
  { v: "high", t: "> 7'000", code: 9000 },
];

// Simplified client-side estimate (mirrors backend logic, lighter)
function estimate({ situation, revenuCode, enfants }) {
  const adultes = situation === "celibataire" ? 1 : 2;
  const plafond = (adultes === 2 ? 75000 : 50000) + enfants * 10000;
  const revenuAnnuel = revenuCode * 12;
  if (revenuAnnuel >= plafond) {
    return { eligible: false, total: 0, message: "Probablement pas éligible aux subsides." };
  }
  const seuilFull = plafond * 0.3;
  const ratio = revenuAnnuel <= seuilFull
    ? 1
    : Math.max(0, 1 - (revenuAnnuel - seuilFull) / (plafond - seuilFull));
  const sub = Math.round((600 * adultes + 110 * enfants) * ratio);
  return {
    eligible: sub > 0,
    total: sub,
    annuel: sub * 12,
    message: sub > 0
      ? `Subside potentiel estimé : ~${formatCHF(sub)} / mois.`
      : "Pas de subside estimé à ce niveau.",
  };
}

export default function ExpressComparator() {
  const [step, setStep] = useState(0);
  const [situation, setSituation] = useState(null);
  const [revenu, setRevenu] = useState(null);
  const [enfants, setEnfants] = useState(null);

  const result = step === 3 && situation && revenu && enfants !== null
    ? estimate({ situation: situation.v, revenuCode: revenu.code, enfants })
    : null;

  const reset = () => {
    setStep(0);
    setSituation(null);
    setRevenu(null);
    setEnfants(null);
  };

  return (
    <div
      data-testid="express-comparator"
      className="relative bg-white border border-slate-200 rounded-2xl p-6 md:p-8 shadow-[0_24px_60px_rgb(0,0,0,0.08)]"
    >
      <div className="flex items-center justify-between">
        <div className="inline-flex items-center gap-2 text-xs tracking-[0.18em] uppercase font-bold text-[#9C6644]">
          <Zap className="w-3.5 h-3.5" strokeWidth={2} />
          Comparateur express · 30s
        </div>
        {step > 0 && step < 3 && (
          <div className="text-xs text-slate-500">{step}/3</div>
        )}
      </div>

      {/* Progress dots */}
      <div className="mt-4 flex gap-1.5">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className={`h-1 flex-1 rounded-full transition-colors ${
              step > i ? "bg-slate-900" : "bg-slate-200"
            }`}
          />
        ))}
      </div>

      <div className="mt-6 min-h-[260px]">
        {step === 0 && (
          <div data-testid="express-step-situation" className="fade-up">
            <h3 className="font-display text-xl text-slate-900 font-medium">
              Quelle est votre situation ?
            </h3>
            <div className="mt-5 grid grid-cols-3 gap-2">
              {situations.map((s) => (
                <button
                  key={s.v}
                  data-testid={`express-situation-${s.v}`}
                  onClick={() => {
                    setSituation(s);
                    setStep(1);
                  }}
                  className="p-4 text-sm rounded-lg border border-slate-200 hover:border-slate-900 hover:bg-slate-50 transition-all text-slate-800 font-medium"
                >
                  {s.t}
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 1 && (
          <div data-testid="express-step-revenu" className="fade-up">
            <h3 className="font-display text-xl text-slate-900 font-medium">
              Revenu mensuel net du foyer (CHF) ?
            </h3>
            <div className="mt-5 grid grid-cols-3 gap-2">
              {revenuRanges.map((r) => (
                <button
                  key={r.v}
                  data-testid={`express-revenu-${r.v}`}
                  onClick={() => {
                    setRevenu(r);
                    setStep(2);
                  }}
                  className="p-4 text-sm rounded-lg border border-slate-200 hover:border-slate-900 hover:bg-slate-50 transition-all text-slate-800 font-medium"
                >
                  {r.t}
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 2 && (
          <div data-testid="express-step-enfants" className="fade-up">
            <h3 className="font-display text-xl text-slate-900 font-medium">
              Avez-vous des enfants à charge ?
            </h3>
            <div className="mt-5 grid grid-cols-4 gap-2">
              {[0, 1, 2, 3].map((n) => (
                <button
                  key={n}
                  data-testid={`express-enfants-${n}`}
                  onClick={() => {
                    setEnfants(n);
                    setStep(3);
                  }}
                  className="p-4 text-sm rounded-lg border border-slate-200 hover:border-slate-900 hover:bg-slate-50 transition-all text-slate-800 font-medium"
                >
                  {n === 0 ? "Aucun" : n === 3 ? "3+" : n}
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 3 && result && (
          <div data-testid="express-result" className="fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-medium">
              <Sparkles className="w-3.5 h-3.5" strokeWidth={2} />
              Résultat instantané
            </div>
            <h3 className="mt-4 font-display text-2xl text-slate-900 font-medium leading-tight">
              {result.eligible
                ? "Vous pourriez économiser"
                : "Audit de votre couverture recommandé"}
            </h3>
            {result.eligible && (
              <div className="mt-4">
                <div className="font-display text-4xl text-[#9C6644] font-medium">
                  {formatCHF(result.annuel)} <span className="text-base text-slate-500">/ an</span>
                </div>
                <div className="text-sm text-slate-600 mt-2">{result.message}</div>
              </div>
            )}
            {!result.eligible && (
              <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                Même sans subside, nous pouvons généralement faire baisser votre prime
                de 10 à 25 % via un comparatif neutre LAMal et LCA.
              </p>
            )}

            <div className="mt-6 flex flex-col gap-2">
              <Button
                asChild
                data-testid="express-cta-simulator"
                className="bg-slate-900 hover:bg-slate-800 w-full h-11"
              >
                <a href="#simulateur">
                  Simulation détaillée
                  <ArrowRight className="w-4 h-4 ml-2" />
                </a>
              </Button>
              <Button
                asChild
                data-testid="express-cta-contact"
                variant="outline"
                className="w-full border-slate-300 h-11"
              >
                <a href="#contact">Être recontacté gratuitement</a>
              </Button>
              <button
                onClick={reset}
                data-testid="express-reset"
                className="text-xs text-slate-500 hover:text-slate-900 mt-1 inline-flex items-center justify-center gap-1"
              >
                <RotateCcw className="w-3 h-3" />
                Recommencer
              </button>
            </div>

            <p className="mt-4 text-[10px] text-slate-400 leading-snug">
              Estimation indicative non contractuelle, basée sur les barèmes
              publics simplifiés du canton de Genève.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
